import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ReactComponent as InteractiveIcon } from 'assets/icons/interactive.svg';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { WIDGET_TYPE_TO_STRING } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { useToast } from 'modules/common/components/Toast';
import { PopoverWrapper } from 'modules/common/components/ToolbarPopover/PopoverWrapper';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Interaction, InteractionType, InteractiveElement } from 'types/interactiveWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { useWidget } from 'widgets/sdk/useWidget';

import { getInteractiveMenuMPEvent, getSupportedInteractions, hasInteraction } from './InteractiveMenu.helpers';
import { ExpandInteractionOption, LabelInteractionOption } from './InteractiveOptions';

// Temporary interactive toolbar menu - will be moved to a right panel menu.
interface InteractiveMenuProps {
  isDisabled?: boolean;
}

export const InteractiveMenu = ({ isDisabled }: InteractiveMenuProps) => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'interactive' });
  const { interactions } = useWidget<InteractiveElement>();
  const isActive = hasInteraction(interactions);

  const label = t('label');
  return (
    <PopoverWrapper>
      <Popover isLazy placement='bottom-start'>
        {({ isOpen }) => (
          <>
            <Tooltip label={label} aria-label={label} hasArrow>
              <Box>
                <PopoverTrigger>
                  <IconButton
                    data-testid='interactive-menu-btn'
                    variant='icon-btn-toolbar'
                    aria-label={label}
                    size='sm'
                    icon={<InteractiveIcon width={20} height={20} />}
                    isActive={(isActive || isOpen) && !isDisabled}
                    isDisabled={isDisabled}
                    _active={{ bg: 'orange.100', color: 'font.500' }}
                  />
                </PopoverTrigger>
              </Box>
            </Tooltip>
            <InteractiveMenuContent />
          </>
        )}
      </Popover>
    </PopoverWrapper>
  );
};

const InteractiveMenuContent = () => {
  const { interactions, updateWidget, widgetId } = useWidget<InteractiveElement>();
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'interactive' });
  const toast = useToast();
  const widgetType = getWidgetTypeFromId(widgetId);

  const label = t('label');
  const description = t('description');

  const supportedInteractions = getSupportedInteractions(widgetType);
  const numCols = supportedInteractions.length >= 2 ? 2 : 1;

  const updateInteraction = useCallback(
    (type: InteractionType, data: Interaction | null) => {
      const mpData = getInteractiveMenuMPEvent(type, data, interactions?.[type] || null);
      if (mpData) {
        const { eventName, eventProps } = mpData;
        Mixpanel.track(eventName, {
          ...eventProps,
          widget_type: WIDGET_TYPE_TO_STRING[widgetType],
        });
      }

      updateWidget({ interactions: { ...interactions, [type]: data } });
      toast({
        status: 'success',
        title: !!data ? t('toast.added') : t('toast.removed'),
      });
    },
    [updateWidget, interactions, toast, widgetType, t],
  );

  return (
    <PopoverContent color='font.500' w={`${12 * numCols}rem`}>
      <VisuallyHidden>
        <PopoverHeader>{label}</PopoverHeader>
      </VisuallyHidden>
      <PopoverBody p={0}>
        <Flex flexDirection='column' p={4} gap={2}>
          <Text fontSize='sm'>{description}</Text>
          <Grid templateColumns={`repeat(${numCols}, 1fr)`} gap={2}>
            {supportedInteractions.includes(InteractionType.Expandable) && (
              <GridItem>
                <ExpandInteractionOption interactions={interactions} updateInteraction={updateInteraction} />
              </GridItem>
            )}
            {supportedInteractions.includes(InteractionType.Tooltip) && (
              <GridItem>
                <LabelInteractionOption interactions={interactions} updateInteraction={updateInteraction} />
              </GridItem>
            )}
          </Grid>
        </Flex>
      </PopoverBody>
    </PopoverContent>
  );
};
